import React, { useEffect, useState, useCallback } from "react";
import { Link, useHistory, useParams } from "react-router-dom";

import Layout from "../../../../../../components/Layout";
import Container from "../../../../../../components/Container";
import Flex from "../../../../../../components/Flex";
import Content from "../../../../../../components/Content";
import Button from "../../../../../../components/Button";
import Tooltip from "../../../../../../components/Tooltip";
import Breadcrumbs from "../../../../../../components/Breadcrumbs";
import { useAuthState } from "../../../../../../components/AuthProvider";
import Spinner from "../../../../../../components/Loader/Spinner";
import DataTable from "../../../../../../components/DataTable";
import LinkButton from "../../../../../../components/LinkButton";
import ButtonList from "../../../../../../components/ButtonList";
import { useMessageToast } from "../../../../../../components/MessageProvider";

import styles from "./ViewSource.module.scss";

const ViewSource = () => {
  const moment = require("moment-timezone");
  const clientConfigApiDomain = process.env.REACT_APP_CLIENT_CONFIG_API_DOMAIN;
  const organizationApiUrl = `${clientConfigApiDomain}/organizations`;
  const clientsApiUrl = `${clientConfigApiDomain}/clients`;
  const sourcesApiUrl = `${clientConfigApiDomain}/sources`;
  const elementTrustScoreApiUrl = `${clientConfigApiDomain}/element_trust_scores`;

  const { id, clientId, sourceId } = useParams();

  const history = useHistory();
  const { user } = useAuthState();
  const toast = useMessageToast();

  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [sourceData, setSourceData] = useState([]);
  const [clientData, setClientData] = useState([]);
  const [organizationData, setOrganizationData] = useState({});
  const [elementTrustData, setElementTrustData] = useState([]);

  const fetchData = useCallback(async () => {
    window.scrollTo(0, 0);
    setIsLoading(true);

    if (sourceId !== undefined) {
      const response = await fetch(`${sourcesApiUrl}/?id=${sourceId}`);
      const data = (await response.json()).results[0];
      setSourceData(data);

      const elementTrustResponse = await fetch(
        `${elementTrustScoreApiUrl}/?source__oid=${data.oid}`
      );
      if (elementTrustResponse.status === 200) {
        const elementTrustData = (await elementTrustResponse.json()).results;
        setElementTrustData(elementTrustData);
      }
    }

    const clientResponse = await fetch(`${clientsApiUrl}/${clientId}/`);
    const clientData = await clientResponse.json();
    setClientData(clientData);

    const organizationResponse = await fetch(`${organizationApiUrl}/${id}/`);
    const organizationData = await organizationResponse.json();
    setOrganizationData(organizationData);

    setIsLoading(false);
    setHasLoaded(true);
  }, [
    sourceId,
    clientsApiUrl,
    clientId,
    organizationApiUrl,
    id,
    sourcesApiUrl,
    elementTrustScoreApiUrl
  ]);

  useEffect(() => {
    if (!hasLoaded) {
      fetchData();
    }
  }, [fetchData, hasLoaded]);

  let loadedBreadcrumbs = "";
  if (isLoading) {
    loadedBreadcrumbs = <Spinner />;
  } else if (hasLoaded) {
    loadedBreadcrumbs = (
      <Breadcrumbs>
        <Breadcrumbs.Item render={() => <Link to="/">Home</Link>} />
        <Breadcrumbs.Item
          render={() => <Link to="/admin">Administration</Link>}
        />
        <Breadcrumbs.Item
          render={() => <Link to="/admin/organizations">Organizations</Link>}
        />
        <Breadcrumbs.Item
          render={() => (
            <Link to={`/admin/organizations/${id}`}>
              Organization: {organizationData.name}
            </Link>
          )}
        />
        <Breadcrumbs.Item
          render={() => (
            <Link to={`/admin/organizations/${id}/accounts/${clientId}`}>
              Account: {clientData.name}
            </Link>
          )}
        />
        <Breadcrumbs.Item last render={() => `Source: ${sourceData.name}`} />
      </Breadcrumbs>
    );
  }

  const restrictionTextMap = {
    sourceRestricted: "Source Restricted",
    policyRestricted: "Policy Restricted",
    noRestriction: "No Restriction"
  };

  const getRestrictionText = restriction => {
    return restrictionTextMap[restriction] || "No Restriction";
  };

  const isEditVisible = orgData => {
    if (orgData.length !== 0)
      return user.role === "Global Admin" || user.orgIds.includes(orgData.id);
  };

  const elementTrustScoreClickableCell = ({
    cell: {
      value,
      row: { original: selectedElementTrust }
    }
  }) => {
    const onClick = () => {
      history.push(
        `/admin/organizations/${id}/accounts/${clientId}/sources/${sourceId}/element_trust/${selectedElementTrust.id}/`
      );
    };
    return (
      <span className={styles.cell} onClick={onClick}>
        {value}
      </span>
    );
  };

  const actionCell = ({
    cell: {
      value,
      row: { original: selectedElementTrust }
    }
  }) => {
    const onEditClick = () => {
      history.push(
        `/admin/organizations/${id}/accounts/${clientId}/sources/${sourceId}/element_trust/${selectedElementTrust.id}/edit/`
      );
    };

    const onDeleteClick = () => {
      fetch(`${elementTrustScoreApiUrl}/${selectedElementTrust.id}/`, {
        method: "delete"
      })
        .then(response => {
          if (!response.ok) throw Error("Failed to delete element.");
          toast.success(
            `Successfully deleted ${selectedElementTrust.element} trust score for this source.`
          );
          setElementTrustData(
            elementTrustData.filter(el => el !== selectedElementTrust)
          );
        })
        .catch(() => {
          toast.error(
            `Error when attempting to delete ${selectedElementTrust.element} trust score for this source.`
          );
        });
    };

    return (
      <ButtonList className={styles.icons}>
        <LinkButton icon="edit" onClick={onEditClick}>
          Edit
        </LinkButton>
        <LinkButton icon="remove" onClick={onDeleteClick}>
          Delete
        </LinkButton>
      </ButtonList>
    );
  };

  const elementTrustColumns = [
    {
      Header: "Element Name",
      accessor: "element",
      Cell: elementTrustScoreClickableCell
    },
    {
      Header: "Trust Score",
      accessor: "trust_score"
    },
    {
      Header: "",
      accessor: "actions",
      disableFilters: true,
      disableSortBy: true,
      Cell: actionCell
    }
  ];

  return (
    <Layout>
      <Container>
        <Flex gutter={3}>
          <Flex.Item span={12}>{loadedBreadcrumbs}</Flex.Item>
          {isLoading ? (
            <Spinner />
          ) : hasLoaded ? (
            <Flex.Item span={12}>
              <Content>
                <h1>Source: {sourceData.name}</h1>
              </Content>
              <div className={styles.base}>
                <div className={styles.detailsFlex}>
                  <h3>Subscription Details</h3>
                  <div>
                    {isEditVisible(organizationData) && (
                      <Button
                        className={styles.margin}
                        primary
                        onClick={() => {
                          history.push(`${sourceId}/edit`);
                        }}
                      >
                        Edit
                      </Button>
                    )}
                  </div>
                </div>
                <div className={styles.details}>
                  <div>
                    <h4>Source ID</h4>
                    <span>{sourceData.id}</span>
                  </div>
                  <div>
                    <h4>Name</h4>
                    <span>{sourceData.name}</span>
                  </div>
                  <div>
                    <h4>OID</h4>
                    <span>{sourceData.oid ? sourceData.oid : " - "}</span>
                  </div>
                  <div>
                    <h4>Code</h4>
                    <span>{sourceData.code ? sourceData.code : " - "}</span>
                  </div>
                  <div>
                    <h4>Assigning Authority OID</h4>
                    <span>{sourceData.assigning_authority_oid}</span>
                  </div>
                  <div>
                    <h4>Default Trust Score</h4>
                    <span>{sourceData.trust_score}</span>
                  </div>
                  <div>
                    <h4>
                      Trusted Master Identifier
                      <Tooltip>
                        <Content>
                          <p>
                            When enabled, same MRN results in a record update.
                            When disabled, matching score determines a record
                            update.
                          </p>
                        </Content>
                      </Tooltip>
                    </h4>
                    {sourceData.trusted_master_identifier ? (
                      <span>Enabled</span>
                    ) : (
                      <span>Disabled</span>
                    )}
                  </div>
                  <div>
                    <h4>
                      Restriction Type
                      <Tooltip>
                        <Content>
                          <p>
                            Restriction type determines how the source is
                            restricted.
                          </p>
                        </Content>
                      </Tooltip>
                    </h4>
                    <span>{getRestrictionText(sourceData.restriction)}</span>
                  </div>
                  <div>
                    <h4>Created By</h4>
                    <span>{sourceData.created_by}</span>
                  </div>
                  <div>
                    <h4>Date Created</h4>
                    <span>
                      {moment(sourceData.created_at)
                        .tz(user.preferences.timeZone)
                        .format("L, LTS z")}
                    </span>
                  </div>
                  <div>
                    <h4>Edited By</h4>
                    <span>{sourceData.updated_by}</span>
                  </div>
                  <div>
                    <h4>Date Updated</h4>
                    <span>
                      {moment(sourceData.updated_at)
                        .tz(user.preferences.timeZone)
                        .format("L, LTS z")}
                    </span>
                  </div>
                </div>
                <div className={styles.elementTrustScores}>
                  <h3>Element Trust Scores</h3>
                  <ButtonList spacing={2} style={{ margin: "1rem 0" }}>
                    <LinkButton
                      icon="add"
                      onClick={() => {
                        history.push(
                          `/admin/organizations/${id}/accounts/${clientId}/sources/${sourceId}/element_trust/add`,
                          []
                        );
                      }}
                    >
                      Add Element Trust Score
                    </LinkButton>
                  </ButtonList>
                  <DataTable
                    columns={elementTrustColumns}
                    data={elementTrustData}
                    hasSorting
                    hasPagination
                  />
                </div>
              </div>
            </Flex.Item>
          ) : null}
        </Flex>
      </Container>
    </Layout>
  );
};

export default ViewSource;
